// hooks/api/placementVendor.queries.ts
import { notifyError, notifySuccess } from "@/lib/notification";
import * as placementVendorService from "@/services/placementVendor.service";
import {
  PlacementVendorCreateType,
  PlacementVendorParamsType,
  PlacementVendorType,
} from "@/services/placementVendor.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";

export const placementVendorsKey = {
  all: ["placementVendors"] as const,
  lists: (filters: PlacementVendorParamsType) =>
    [...placementVendorsKey.all, "lists", filters] as const,
  detail: (id: string) => [...placementVendorsKey.all, "detail", id] as const,
};

export const usePlacementVendorsQuery = (filters: PlacementVendorParamsType) =>
  useQuery({
    queryKey: placementVendorsKey.lists(filters),
    queryFn: () => placementVendorService.fetchAllPlacementVendors(filters),
    placeholderData: (prev) => prev,
  });

export const usePlacementVendorByIdQuery = (id: string, enabled = true) =>
  useQuery({
    queryKey: placementVendorsKey.detail(id),
    queryFn: () => placementVendorService.fetchPlacementVendorById(id),
    enabled: enabled && !!id,
  });

export const useCreatePlacementVendorMutation = (
  filters: PlacementVendorParamsType
) => {
  const queryClient = useQueryClient();
  return useMutation<
    PlacementVendorType,
    AxiosError,
    PlacementVendorCreateType
  >({
    mutationFn: (data) => placementVendorService.createPlacementVendor(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: placementVendorsKey.lists(filters),
      });
      notifySuccess("Placement vendor added successfully");
    },
    onError: (error: any) =>
      notifyError(
        error?.response?.data?.message || "Failed to add placement vendor"
      ),
  });
};

export const useUpdatePlacementVendorMutation = (
  filters: PlacementVendorParamsType
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      _id,
      data,
    }: {
      _id: string;
      data: PlacementVendorCreateType;
    }) => placementVendorService.updatePlacementVendor(_id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: placementVendorsKey.lists(filters),
      });
      queryClient.invalidateQueries({
        queryKey: placementVendorsKey.detail(variables._id),
      });
      notifySuccess("Placement vendor updated successfully");
    },
    onError: (error: any) =>
      notifyError(
        error?.response?.data?.message || "Failed to update placement vendor"
      ),
  });
};

export const useDeletePlacementVendorMutation = (
  filters: PlacementVendorParamsType
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (_id: string) =>
      placementVendorService.deletePlacementVendor(_id),
    onSuccess: (_, _id) => {
      queryClient.invalidateQueries({
        queryKey: placementVendorsKey.lists(filters),
      });
      queryClient.removeQueries({ queryKey: placementVendorsKey.detail(_id) });
      notifySuccess("Placement vendor deleted successfully");
    },
    onError: (error: any) =>
      notifyError(
        error?.response?.data?.message || "Failed to delete placement vendor"
      ),
  });
};
